'use client'

import { DEFAULT_UNIT_OF_MEASURE, formatPriceWithUnit, getUnitDisplay, resolveUnitOfMeasure } from '@/lib/types/core/unit-of-measure'
import { UnitOfMeasure } from '@prisma/client'
import { useMemo } from 'react'

// Define length-based units
const lengthUnits = [
  UnitOfMeasure.METER,
  UnitOfMeasure.YARD,
  UnitOfMeasure.FOOT,
  UnitOfMeasure.INCH,
  UnitOfMeasure.CENTIMETER,
  UnitOfMeasure.MILLIMETER
] as const

// Helper function to check if a unit is a length unit
const isLengthUnit = (unit: UnitOfMeasure): boolean => {
  return lengthUnits.includes(unit as typeof lengthUnits[number])
}

// Helper to get plural unit display
const getPluralUnitDisplay = (unitDisplay: string): string => {
  if (unitDisplay === 'each') return 'units'
  if (unitDisplay === 'roll') return 'rolls'
  if (unitDisplay === 'unit') return 'units'
  return unitDisplay + 's'
}

// Helper to determine unit system
const getUnitSystem = (unit: UnitOfMeasure): 'imperial' | 'metric' => {
  switch (unit) {
    case UnitOfMeasure.METER:
    case UnitOfMeasure.CENTIMETER:
    case UnitOfMeasure.MILLIMETER:
    case UnitOfMeasure.METRIC:
    case UnitOfMeasure.SQUAREMETER:
      return 'metric'
    case UnitOfMeasure.YARD:
    case UnitOfMeasure.FOOT:
    case UnitOfMeasure.INCH:
    case UnitOfMeasure.IMPERIAL:
    case UnitOfMeasure.SQUAREFOOT:
    case UnitOfMeasure.SQUAREYARD:
      return 'imperial'
    default:
      return 'metric'
  }
}

// Conversion cache for client-side performance optimization
const conversionCache = new Map<string, number>();

// Hardcoded conversion factors for client-side fallback
// @deprecated Use UnitConversionService for database-backed conversions
const hardcodedConversions: Record<string, Record<string, number>> = {
  // Length conversions
  [UnitOfMeasure.METER]: {
    [UnitOfMeasure.YARD]: 1.09361,
    [UnitOfMeasure.FOOT]: 3.28084,
    [UnitOfMeasure.INCH]: 39.37,
    [UnitOfMeasure.CENTIMETER]: 100,
    [UnitOfMeasure.MILLIMETER]: 1000
  },
  [UnitOfMeasure.YARD]: {
    [UnitOfMeasure.METER]: 0.9144,
    [UnitOfMeasure.FOOT]: 3,
    [UnitOfMeasure.INCH]: 36
  },
  [UnitOfMeasure.FOOT]: {
    [UnitOfMeasure.METER]: 0.3048,
    [UnitOfMeasure.YARD]: 0.333333,
    [UnitOfMeasure.INCH]: 12
  },
  [UnitOfMeasure.INCH]: {
    [UnitOfMeasure.METER]: 0.0254,
    [UnitOfMeasure.YARD]: 0.027778,
    [UnitOfMeasure.FOOT]: 0.083333
  },
  [UnitOfMeasure.CENTIMETER]: {
    [UnitOfMeasure.METER]: 0.01
  },
  [UnitOfMeasure.MILLIMETER]: {
    [UnitOfMeasure.METER]: 0.001
  },

  // Area conversions
  [UnitOfMeasure.SQUAREMETER]: {
    [UnitOfMeasure.SQUAREFOOT]: 10.7639,
    [UnitOfMeasure.SQUAREYARD]: 1.19599
  },
  [UnitOfMeasure.SQUAREFOOT]: {
    [UnitOfMeasure.SQUAREMETER]: 0.092903,
    [UnitOfMeasure.SQUAREYARD]: 0.111111
  },
  [UnitOfMeasure.SQUAREYARD]: {
    [UnitOfMeasure.SQUAREMETER]: 0.836127,
    [UnitOfMeasure.SQUAREFOOT]: 9
  }
}

/**
 * Custom hook that manages unit of measure operations consistently across components.
 * Provides utilities for working with different measurement systems (metric/imperial),
 * display formatting, unit conversions, and unit type detection.
 *
 * @param initialUnit - The initial unit of measure to use, typically from product/variant
 * @param variant - Optional variant object to extract unit information from
 * @param product - Optional product object for fallback unit information
 * @returns {UnitOfMeasureHook} Various unit utilities and formatting functions
 *
 * @example
 * // Basic usage with product
 * const { formatPrice, unitDisplay } = useUnitOfMeasure(product.unitOfMeasure, null, product);
 *
 * // With variant
 * const { isLengthBased, formatQuantity } = useUnitOfMeasure(variant.unitOfMeasure, variant);
 *
 * // Using the formatting methods
 * return (
 *   <div>
 *     <p>Price: {formatPrice(basePrice)}</p>
 *     <p>Quantity: {formatQuantity(quantity)}</p>
 *     {isLengthBased && <LengthSelector />}
 *   </div>
 * );
 */
export function useUnitOfMeasure(initialUnit?: UnitOfMeasure, variant?: any, product?: any) {
  // Always prioritize product's unit of measure if available
  const unitOfMeasure = product?.unitOfMeasure ||
    initialUnit ||
    (variant ? resolveUnitOfMeasure(variant) : DEFAULT_UNIT_OF_MEASURE)

  const unitDisplay = getUnitDisplay(unitOfMeasure)
  const pluralUnitDisplay = getPluralUnitDisplay(unitDisplay)
  const unitSystem = getUnitSystem(unitOfMeasure)
  const isLengthBased = isLengthUnit(unitOfMeasure)

  // Convert between units (hardcoded for client-side compatibility)
  // Memoized conversion function for performance
  const convert = useMemo(() => {
    return (value: number, fromUnit: UnitOfMeasure, toUnit: UnitOfMeasure): number => {
      if (fromUnit === toUnit) return value;

      const cacheKey = `${fromUnit}->${toUnit}`;
      
      // Check cache first
      if (conversionCache.has(cacheKey)) {
        return value * conversionCache.get(cacheKey)!;
      }

      // Calculate conversion factor
      let factor = 1;
      if (hardcodedConversions[fromUnit]?.[toUnit]) {
        factor = hardcodedConversions[fromUnit][toUnit];
      }

      // Cache the factor for future use
      conversionCache.set(cacheKey, factor);
      
      return value * factor;
    };
  }, []); // Empty dependency array since conversion factors are static

  // Format price with unit
  const formatPrice = (price: number): string => {
    return formatPriceWithUnit(price, unitOfMeasure)
  }

  // Format quantity with unit
  const formatQuantity = (quantity: number): string => {
    return quantity === 1 ? `1 ${unitDisplay}` : `${quantity} ${pluralUnitDisplay}`
  }

  // Format value with unit
  const formatValueWithUnit = (value: number): string => {
    if (isLengthBased) {
      return `${value} ${value === 1 ? unitDisplay : pluralUnitDisplay}`
    }
    if (unitOfMeasure === UnitOfMeasure.ROLL) {
      return `${value} ${value === 1 ? 'roll' : 'rolls'}`
    }
    return `${value} ${value === 1 ? 'unit' : 'units'}`
  }

  return {
    unitOfMeasure,
    unitDisplay,
    pluralUnitDisplay,
    unitSystem,
    isImperial: unitSystem === 'imperial',
    isMetric: unitSystem === 'metric',
    isLengthBased,
    isQuantityBased: !isLengthBased,
    convert,
    formatPrice,
    formatQuantity,
    formatValueWithUnit
  }
}

export default useUnitOfMeasure
